import axios from "axios";
import GenerateToken from "./token";
import api from "./api";

const handleSessionExpiration = (error) => {
  if (
    error?.response?.data?.response === 401 &&
    error?.response?.data?.status === false &&
    error?.response?.data?.message === "Session expired. Please log in again."
  ) {
    setTimeout(() => {
      localStorage.removeItem("admin");
      window.location.reload();
    }, 2000);

    return {
      sessionExpired: true,
      message: "Session expired. Please log-in again.",
    };
  }

  throw error;
};

function getStoredAdmin() {
  try {
    return JSON.parse(localStorage.getItem("admin") || "null");
  } catch {
    return null;
  }
}

function getStoredDynamicKey() {
  return getStoredAdmin()?.dynamic_key || "";
}

function getStoredLoginProvider() {
  return getStoredAdmin()?.login_provider || "";
}

function buildAuthHeaders(token, contentType) {
  const headers = {
    "x-api-key": import.meta.env.VITE_X_API_KEY,
    "Content-Type": contentType,
    Authorization: token ? GenerateToken(token) : "",
  };
  const dynKey = getStoredDynamicKey();
  if (dynKey) {
    headers["x-dynamic-key"] = dynKey;
  }
  return headers;
}

// Llama POST /v1/refresh-dynamic-key con el JWT actual y devuelve el
// nuevo dynamic_key. Devuelve null si falla.
async function tryRefreshDynamicKey() {
  const stored = getStoredAdmin();
  const token = stored?.token;
  if (!token) return null;
  try {
    const res = await axios({
      method: "post",
      url: `${api}/refresh-dynamic-key`,
      headers: {
        "x-api-key": import.meta.env.VITE_X_API_KEY,
        "Content-Type": "application/json",
        Authorization: GenerateToken(token),
      },
    });
    return res?.data?.dynamic_key || null;
  } catch {
    return null;
  }
}

function persistNewDynamicKey(newKey) {
  const stored = getStoredAdmin();
  if (!stored) return;
  stored.dynamic_key = newKey;
  localStorage.setItem("admin", JSON.stringify(stored));
}

// In-flight refresh promise (single-flight): parallel 401s share one
// /v1/refresh call so they don't all try to redeem the same refresh_token
// (server enforces single-active-refresh).
let _refreshPromise = null;

async function tryRefreshSessionToken() {
  if (_refreshPromise) return await _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const stored = getStoredAdmin();
      const refreshToken = stored?.refresh_token;
      const userId = stored?.id;
      if (!refreshToken || !userId) return null;
      const res = await axios({
        method: "post",
        url: `${api}/refresh`,
        headers: {
          "x-api-key": import.meta.env.VITE_X_API_KEY,
          "Content-Type": "application/json",
        },
        data: { id: userId, refresh_token: refreshToken },
      });
      const data = res?.data;
      if (!data?.status || !data?.token || !data?.refresh_token) return null;
      const updated = {
        ...stored,
        ...data.data,
        token: data.token,
        refresh_token: data.refresh_token,
        refresh_token_created_at: data.refresh_token_created_at,
        session_token_created_at: data.session_token_created_at,
        dynamic_key: data.dynamic_key || stored.dynamic_key,
        login_provider: data.login_provider || stored.login_provider,
      };
      localStorage.setItem("admin", JSON.stringify(updated));
      return data.token;
    } catch {
      return null;
    } finally {
      _refreshPromise = null;
    }
  })();
  return await _refreshPromise;
}

// Wrapper que detecta:
//   - 401 + X-Auth-Reason: dynamic-key → flow original (google refresh-key).
//   - 401 sin X-Auth-Reason → JWT vencido → tryRefreshSession + retry.
async function executeWithDynamicKeyRetry(config) {
  try {
    return await axios(config);
  } catch (error) {
    const status = error?.response?.status;
    const reason = error?.response?.headers?.["x-auth-reason"];

    if (status === 401 && reason === "dynamic-key" && !config._dynKeyRetried) {
      config._dynKeyRetried = true;
      const provider = getStoredLoginProvider();
      if (provider === "google") {
        const newKey = await tryRefreshDynamicKey();
        if (newKey) {
          persistNewDynamicKey(newKey);
          config.headers = config.headers || {};
          config.headers["x-dynamic-key"] = newKey;
          return await axios(config);
        }
      }
      localStorage.removeItem("admin");
      window.location.href = "/admin/login";
      throw error;
    }

    if (status === 401 && reason !== "dynamic-key" && !config._sessionRetried) {
      config._sessionRetried = true;
      const newToken = await tryRefreshSessionToken();
      if (newToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = GenerateToken(newToken);
        const newDynKey = getStoredDynamicKey();
        if (newDynKey) config.headers["x-dynamic-key"] = newDynKey;
        return await axios(config);
      }
      localStorage.removeItem("admin");
      window.location.href = "/admin/login";
      throw error;
    }

    throw error;
  }
}

const GET = async (token, endPoint) => {
  const config = {
    method: "get",
    maxBodyLength: Infinity,
    url: `${api}/${endPoint}`,
    headers: buildAuthHeaders(token, "application/json"),
  };

  try {
    const response = await executeWithDynamicKeyRetry(config);
    return response.data;
  } catch (error) {
    return handleSessionExpiration(error);
  }
};

const ADD = async (token, endPoint, data) => {
  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: `${api}/${endPoint}`,
    headers: buildAuthHeaders(token, "multipart/form-data"),
    data,
  };

  try {
    const response = await executeWithDynamicKeyRetry(config);
    return response.data;
  } catch (error) {
    return handleSessionExpiration(error);
  }
};

const ADDMulti = async (token, url, data) => {
  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url,
    headers: buildAuthHeaders(token, "multipart/form-data"),
    data,
  };

  try {
    const response = await executeWithDynamicKeyRetry(config);
    return response.data;
  } catch (error) {
    return handleSessionExpiration(error);
  }
};

const UPDATE = async (token, endPoint, data) => {
  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: `${api}/${endPoint}`,
    headers: buildAuthHeaders(token, "multipart/form-data"),
    data,
  };

  try {
    const response = await executeWithDynamicKeyRetry(config);
    return response.data;
  } catch (error) {
    return handleSessionExpiration(error);
  }
};

const DELETE = async (token, endPoint, data) => {
  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: `${api}/${endPoint}`,
    headers: buildAuthHeaders(token, "application/json"),
    data,
  };

  try {
    const response = await executeWithDynamicKeyRetry(config);
    return response.data;
  } catch (error) {
    return handleSessionExpiration(error);
  }
};

const UPLOAD = async (token, url, data) => {
  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url,
    headers: buildAuthHeaders(token, "multipart/form-data"),
    data,
  };

  try {
    const response = await executeWithDynamicKeyRetry(config);
    return response.data;
  } catch (error) {
    return handleSessionExpiration(error);
  }
};

// REST verdaderos (no Laravel-style). Usados por endpoints panel TV
// (`/v1/clinics/:id/videos/:videoId` PATCH/DELETE, `/v1/user-clinics/:id/...`
// PATCH, etc.) donde el backend medicare-node-api respeta el method HTTP en
// lugar del POST-everything de Laravel.
const POST_JSON = async (token, endPoint, data) => {
  const config = {
    method: "post",
    maxBodyLength: Infinity,
    url: `${api}/${endPoint}`,
    headers: buildAuthHeaders(token, "application/json"),
    data,
  };
  try {
    const response = await executeWithDynamicKeyRetry(config);
    return response.data;
  } catch (error) {
    return handleSessionExpiration(error);
  }
};

const PATCH = async (token, endPoint, data) => {
  const config = {
    method: "patch",
    maxBodyLength: Infinity,
    url: `${api}/${endPoint}`,
    headers: buildAuthHeaders(token, "application/json"),
    data,
  };
  try {
    const response = await executeWithDynamicKeyRetry(config);
    return response.data;
  } catch (error) {
    return handleSessionExpiration(error);
  }
};

const DELETE_REST = async (token, endPoint) => {
  const config = {
    method: "delete",
    maxBodyLength: Infinity,
    url: `${api}/${endPoint}`,
    headers: buildAuthHeaders(token, "application/json"),
  };
  try {
    const response = await executeWithDynamicKeyRetry(config);
    return response.data;
  } catch (error) {
    return handleSessionExpiration(error);
  }
};

export { GET, ADD, DELETE, UPDATE, UPLOAD, ADDMulti, POST_JSON, PATCH, DELETE_REST };
